import React from "react";
import { useFormikContext } from "formik";
import { CSSProperties } from "react";

import { IControlStyles } from "../index";
import { useTheme } from "../../../../theme/ThemeProvider";

export interface ControlAttachmentsProps {
    name: string;
    selectedFiles: File[];
    onFilesChange: (files: File[]) => void;
    accept?: string;
    isLoading?: boolean;
    label?: string;
    note?: string;
    disabled?: boolean;
    isOptional?: boolean;
    tooltip?: string;
    controlStyles?: IControlStyles;
}

const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) return "bi-file-earmark-image primary";
    if (mimeType === "application/pdf") return "bi-file-earmark-pdf danger";
    if (mimeType.includes("word")) return "bi-file-earmark-word primary";
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "bi-file-earmark-spreadsheet success";
    if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "bi-file-earmark-slides orange";
    return "bi-file-earmark-text secondary";
};

export const ControlAttachments: React.FC<ControlAttachmentsProps> = (props) => {
    const theme = useTheme();
    const { setFieldValue } = useFormikContext<any>();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

    const styles = {
        wrapper: {
            marginTop: theme.spacePx.sp01,
        } as CSSProperties,

        card: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacePx.sp03,
            background: theme.palette.background.main,
            border: `1px solid ${theme.palette.divider.light}`,
            borderRadius: `calc(${theme.shape.borderRadiusMd}px)`,
            padding: `${theme.spacePx.sp03} ${theme.spacePx.sp04}`,
            marginBottom: theme.spacePx.sp02,
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        } as CSSProperties,

        iconWrapper: {
            width: "40px",
            height: "40px",
            background: theme.palette.background.light,
            borderRadius: theme.spacePx.sp02,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: theme.font.sizes.lg,
            color: theme.palette.text.main,
            flexShrink: 0,
        } as CSSProperties,

        info: {
            flexGrow: 1,
            minWidth: 0,
        } as CSSProperties,

        fileName: {
            display: "block",
            fontWeight: theme.font.weights.semiBold,
            color: theme.palette.text.dark,
            fontSize: theme.font.sizes.md,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        } as CSSProperties,

        fileMeta: {
            fontSize: theme.font.sizes.xs,
            color: theme.palette.grey,
        } as CSSProperties,

        removeBtn: {
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: theme.spacePx.sp01,
            color: theme.palette.grey,
            cursor: "pointer",
            transition: "all 0.2s",
            background: "transparent",
            border: "none",
        } as CSSProperties,

        addButton: {
            width: "100%",
            padding: theme.spacePx.sp02,
            background: theme.palette.background.light,
            border: `2px dashed ${theme.palette.divider.main}`,
            borderRadius: `calc(${theme.shape.borderRadiusMd}px * 2)`,
            color: theme.palette.text.main,
            fontWeight: theme.font.weights.semiBold,
            fontSize: theme.font.sizes.sm,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacePx.sp02,
            cursor: "pointer",
            transition: "all 0.2s",
            marginTop: theme.spacePx.sp01,
        } as CSSProperties,
    };


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileArray = e.target.files ? Array.from(e.target.files) : [];
        if (fileArray.length === 0) return;

        const combinedFiles = [...props.selectedFiles, ...fileArray];
        props.onFilesChange(combinedFiles);
        setFieldValue(props.name, combinedFiles);

        if (e.target) e.target.value = "";
    };

    const handleRemoveFile = (index: number) => {
        const updatedFiles = props.selectedFiles.filter((_, i) => i !== index);
        props.onFilesChange(updatedFiles);
        setFieldValue(props.name, updatedFiles);
    };

    return (
        <div style={styles.wrapper}>
            {props.label && <label className="form-label mb-1">{props.label}</label>}
            {props.note && (
                <div
                    className="small text-muted mb-2 d-flex align-items-center"
                    style={{ opacity: 0.8, fontSize: theme.font.sizes.sm }}
                >
                    <i className="bi bi-info-circle-fill me-1 text-primary" style={{ fontSize: theme.font.sizes.sm }} />
                    {props.note}
                </div>
            )}

            <div>
                {props.selectedFiles.map((file, i) => (
                    <div
                        key={i}
                        style={{ ...styles.card, ...(hoveredIndex === i ? { borderColor: theme.palette.information.main, boxShadow: theme.shadowTypes.popover } : {}) }}
                        onMouseEnter={() => setHoveredIndex(i)}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        <div style={styles.iconWrapper}>
                            <i className={`bi ${getFileIcon(file.type)}`} />
                        </div>
                        <div style={styles.info}>
                            <span style={styles.fileName} title={file.name}>{file.name}</span>
                            <span style={styles.fileMeta}>
                                {(file.size / 1024).toFixed(1)} KB
                            </span>
                        </div>
                        {!props.isLoading && (
                            <button
                                type="button"
                                style={styles.removeBtn}
                                onClick={() => handleRemoveFile(i)}
                                title="Remove file"
                            >
                                <i className="bi bi-trash3-fill" />
                            </button>
                        )}
                    </div>
                ))}

                {!props.isLoading && (
                    <button
                        type="button"
                        style={styles.addButton}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <i className="bi bi-plus-lg" />
                        {props.selectedFiles.length > 0 ? "Add Another Attachment" : "Add Attachment"}
                    </button>
                )}

                <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    accept={props.accept}
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
};